var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-lines */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { CSSTransition } from 'react-transition-group';
import { CFBaseToastTokens, CFBaseToastUtils, TOAST_DEFAULT_DURATION, TOAST_STATUS, } from '@connectrn/ds-ui-base';
import { CFAvatar, CFButton, CFIcon, CFText } from '../../components';
import { commonAttrs } from '../../utils';
import { CFToastActionsSpacer } from './ToastActionsSpacer';
import { CFToastElements } from './Toast.styles';
export const CFToastCore = (_a, ref) => {
    var { actions, alternateTheme = true, avatar, duration = TOAST_DEFAULT_DURATION, hideClose, icon, id, message, messageTypeSpec, onCancel, onCreate, onDismiss, onRemove, onStart, onStop, onTimeout, startOnCreate = true, status = 'message', title, titleTypeSpec } = _a, rest = __rest(_a, ["actions", "alternateTheme", "avatar", "duration", "hideClose", "icon", "id", "message", "messageTypeSpec", "onCancel", "onCreate", "onDismiss", "onRemove", "onStart", "onStop", "onTimeout", "startOnCreate", "status", "title", "titleTypeSpec"]);
    const { testID = 'toast' } = rest;
    const activeTimeout = useRef(null);
    const { Actions, ActionsSpacer, IconWrapper, MainWrapper, Message, Root } = CFToastElements;
    const [isActive, setIsActive] = useState(false);
    // Manually remove toast
    const cancel = () => {
        if (onCancel)
            onCancel(id);
        remove({ status: TOAST_STATUS.CANCELLED });
    };
    // Toast is removed internally or through expected user interaction
    const dismiss = () => {
        if (onDismiss)
            onDismiss(id);
        remove({ status: TOAST_STATUS.DISMISSED });
    };
    // Toast is removed
    const remove = (payload) => {
        if (activeTimeout.current) {
            clearTimeout(activeTimeout.current);
            activeTimeout.current = null;
        }
        setIsActive(false);
        if (onRemove) {
            onRemove(id, payload);
        }
    };
    // Starts a timeout
    const start = () => {
        if (onStart)
            onStart(id);
        timeout();
    };
    // Stops a timeout in motion; does not remove the toast
    const stop = () => {
        if (activeTimeout.current) {
            clearTimeout(activeTimeout.current);
            activeTimeout.current = null;
        }
        if (onStop)
            onStop(id);
    };
    // Starts a timeout function that will auto-dismiss after duration expires
    const timeout = () => {
        if (!duration)
            return null;
        if (!isActive || activeTimeout.current)
            return true;
        activeTimeout.current = setTimeout(() => {
            activeTimeout.current = null;
            if (onTimeout)
                onTimeout(id);
            remove({ status: TOAST_STATUS.TIMEOUT });
        }, duration);
    };
    // Respond to handle close click
    const handleCloseClick = () => {
        dismiss();
    };
    // Release onCreate hook
    useEffect(() => {
        setIsActive(true);
    }, []);
    useEffect(() => {
        if (!isActive) {
            return;
        }
        if (startOnCreate)
            start();
        if (onCreate)
            onCreate(id);
    }, [isActive]);
    // Expose handles for sending commands from the outside
    useImperativeHandle(ref, () => {
        return {
            cancel,
            dismiss,
            id,
            start,
            stop,
        };
    });
    //
    // Render functions
    //
    const renderActions = () => {
        const actionRenderer = (actionConfigs, i) => actionConfigs === CFToastActionsSpacer ? (React.createElement(ActionsSpacer, { key: `toast-action-${i}`, "aria-hidden": true })) : (React.createElement(CFButton, Object.assign({ key: `toast-action-${i}`, size: 'xs', variant: 'subtle', rounded: true, testID: `${testID}-action-${i}` }, actionConfigs)));
        return (actions &&
            actions.length > 0 && (React.createElement(Actions, { alternateTheme: alternateTheme }, actions.map(actionRenderer))));
    };
    const renderIcon = () => (icon !== null || avatar) && (React.createElement(IconWrapper, { alternateTheme: alternateTheme, hasAvatar: !!avatar, status: status }, status === 'member' && avatar ? (
    // If testID is specified in AvatarProps, it overrides this default.
    React.createElement(CFAvatar, Object.assign({ testID: `${testID}-avatar` }, avatar))) : (React.createElement(CFIcon, { color: CFBaseToastUtils.getIconColor({
            alternateTheme,
            status,
        }), icon: icon || CFBaseToastUtils.getIconPreset(status), size: CFBaseToastUtils.getIconSize(status), testID: `${testID}-icon` }))));
    const renderText = () => (React.createElement(Message, null,
        React.createElement(CFText, { color: CFBaseToastUtils.getTitleColor({
                alternateTheme,
                status,
            }), spec: titleTypeSpec || CFBaseToastTokens.TYPE_SPEC_PRESET.TITLE, testID: `${testID}-title` }, title),
        message && (React.createElement(CFText, { color: CFBaseToastUtils.getMessageColor({ alternateTheme }), spec: messageTypeSpec || CFBaseToastTokens.TYPE_SPEC_PRESET.MESSAGE, testID: `${testID}-message` }, message))));
    return (React.createElement(CSSTransition, { in: isActive, timeout: CFBaseToastTokens.TRANSITION_DURATION, addEndListener: () => null }, (state) => (React.createElement(Root, Object.assign({ alternateTheme: alternateTheme, role: 'toast', status: status, transitionState: state }, commonAttrs(rest)),
        React.createElement(MainWrapper, { alternateTheme: alternateTheme },
            renderIcon(),
            renderText(),
            !hideClose && (React.createElement(CFButton, { accessibleLabel: 'close-toast', iconLeft: 'close', onClick: handleCloseClick, size: 'sm', testID: `${testID !== null && testID !== void 0 ? testID : 'toast'}-close-button`, variant: CFBaseToastUtils.getCloseButtonVariant(alternateTheme) }))),
        renderActions()))));
};
export const CFToast = forwardRef(CFToastCore);
